import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Dialog,
  Illustration,
  Input,
  toast,
  type IllustrationName,
} from '@node-space/ui-components'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { FieldMaxLength } from 'constants/forms'
import { useWalletActionMutation } from 'hooks/mutations/useWalletActionMutation'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { WalletLifecycleActionTypes, type WalletLifecycleAction } from 'types/wallets'

interface WalletActionsDialogProps {
  walletId: string
  walletName: string
  action: WalletLifecycleAction
  onClose: () => void
  onSuccess?: () => void
  invalidateQueryKeys?: string[][]
}

interface WalletActionDialogConfig {
  titleKey: string
  descriptionKey: string
  confirmLabelKey: string
  toastKey: string
  sentryLabel: string
  illustration: IllustrationName
  isDestructive: boolean
}

const walletActionDialogConfig: Record<WalletLifecycleAction, WalletActionDialogConfig> = {
  [WalletLifecycleActionTypes.BLOCK]: {
    titleKey: 'wallets.actions.blockDialogTitle',
    descriptionKey: 'wallets.actions.blockDialogDescription',
    confirmLabelKey: 'wallets.actions.block',
    toastKey: 'wallets.actions.blockSuccess',
    sentryLabel: 'blockWallet',
    illustration: 'Prohibit',
    isDestructive: false,
  },
  [WalletLifecycleActionTypes.UNBLOCK]: {
    titleKey: 'wallets.actions.unblockDialogTitle',
    descriptionKey: 'wallets.actions.unblockDialogDescription',
    confirmLabelKey: 'wallets.actions.unblock',
    toastKey: 'wallets.actions.unblockSuccess',
    sentryLabel: 'unblockWallet',
    illustration: 'SealCheck',
    isDestructive: false,
  },
  [WalletLifecycleActionTypes.TERMINATE]: {
    titleKey: 'wallets.actions.terminateDialogTitle',
    descriptionKey: 'wallets.actions.terminateDialogDescription',
    confirmLabelKey: 'wallets.actions.terminate',
    toastKey: 'wallets.actions.terminateSuccess',
    sentryLabel: 'terminateWallet',
    illustration: 'Warning',
    isDestructive: true,
  },
}

export const WalletActionsDialog = ({
  walletId,
  walletName,
  action,
  onClose,
  onSuccess,
  invalidateQueryKeys = [
    ['walletsV2'],
    ['wallets'],
    [reactQueryName.BULK_CAPABILITIES],
    ['totalBalance'],
    [reactQueryName.CUSTOMER_WALLETS],
  ],
}: WalletActionsDialogProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutate: submitWalletAction, isPending } = useWalletActionMutation()
  const [reason, setReason] = useState('')
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const actionConfig = walletActionDialogConfig[action]
  const isReasonEmpty = !reason.trim()
  const showReasonError = hasAttemptedSubmit && isReasonEmpty

  const handleConfirm = () => {
    setHasAttemptedSubmit(true)
    if (isReasonEmpty) return

    submitWalletAction(
      { walletId, action, comment: reason.trim() },
      {
        onSuccess: () => {
          toast({
            header: t(actionConfig.toastKey, { walletName }),
            variant: 'success',
          })
          invalidateQueryKeys.forEach(key => {
            queryClient.invalidateQueries({ queryKey: key })
          })
          onSuccess?.()
          onClose()
        },
        onError: error => {
          logSentryErrorAndGetEventId(actionConfig.sentryLabel, error)
          toast({
            header: t('wallets.actions.actionError'),
            variant: 'error',
          })
        },
      }
    )
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(actionConfig.titleKey, { walletName })}
      Illustration={<Illustration name={actionConfig.illustration} />}
      ActionButtons={[
        <Button
          key="confirm"
          onClick={handleConfirm}
          loading={isPending}
          {...(actionConfig.isDestructive && { variant: 'destructive' })}
        >
          {t(actionConfig.confirmLabelKey)}
        </Button>,
        <Button key="cancel" onClick={onClose} variant="secondary">
          {t('cancel')}
        </Button>,
      ]}
    >
      <Box flexCol gapY={16}>
        <p className="text-foreground-prominent text-body-regular text-center">
          {t(actionConfig.descriptionKey)}
        </p>
        <Input
          testId="wallet-action-reason-input"
          label={t('wallets.actions.reasonLabel')}
          placeholderText={t('wallets.actions.reasonPlaceholder')}
          value={reason}
          maxLength={FieldMaxLength.LONG}
          onChange={e => setReason(e?.target?.value)}
          error={showReasonError}
          errorText={
            showReasonError
              ? t('fieldRequired', { field: t('wallets.actions.reasonLabel') })
              : undefined
          }
        />
      </Box>
    </Dialog>
  )
}
